import { createSelector } from 'reselect';

import { CACHE_SIZE_ARRAY, createDenormalizedSelector, createKeySelector } from 'Lib/reduxUtils';

import { profileArray } from '../entities/profiles.schema';
import { selectEntitiesForProfiles } from '../entities/profiles.selectors';

/**
 * Gets the likes you namespace from the state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {LikesYouState} entities by type
 */
export const selectLikesYou = createKeySelector('likesYou');

/**
 * Gets the number of fast matches as user has (or range if `isRange` is set)
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} number of people who liked you
 */
export const getLikesYouCount = createSelector(selectLikesYou, (likesYou) => likesYou.count);

/**
 * Gets whether we're currently fetching the fast match count
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} whether we're fetching the count
 */
export const getIsLikesYouCountLoading = createSelector(
  selectLikesYou,
  (likesYou) => likesYou.countLoading
);

/**
 * Gets whether the fast matches count is a range
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} whether the fast match count is a range
 */
export const getLikesYouIsRange = createSelector(selectLikesYou, (likesYou) => likesYou.isRange);

/**
 * Gets teaser url
 * @param  {GlobalStateType} state - top-level state
 * @return {string} teaser url
 */
export const getLikesYouTeaserUrl = createSelector(
  selectLikesYou,
  (likesYou) => likesYou.teaserUrl
);

/**
 * Gets the list of fast match IDs
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Array<string>} IDs of fast matches
 */
export const getFastMatchIds = createSelector(selectLikesYou, (likesYou) => likesYou.fastMatches);

/**
 * Gets the list of fast match profiles
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Array<User>>} Fast match profiles
 */

export const getFastMatches = createDenormalizedSelector(
  getFastMatchIds,
  selectEntitiesForProfiles,
  profileArray
);

/**
 * Gets the last fast match profile ID
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} Fast match profile ID
 */
export const getLastFastMatch = createSelector(
  selectLikesYou,
  (likesYou) => likesYou.lastFastMatch
);

/**
 * Gets the last tab on gold home page
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} last tab name
 */
export const getLastGoldHomeTab = createSelector(selectLikesYou, (likesYou) => likesYou.lastTab);

/**
 * Gets if user has visited Gold Home
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} whether user has been to gold home
 */
export const getGoldHomeSeen = createSelector(selectLikesYou, (likesYou) => likesYou.goldHomeSeen);

/**
 * Gets number of new likes since last session
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} number of new likes since last session
 */
export const getNewLikesCount = createSelector(
  selectLikesYou,
  (likesYou) => likesYou?.newLikesCount ?? 0
);

export const getPrevSessionLikesCount = createSelector(
  selectLikesYou,
  (likesYou) => likesYou?.prevSessionLikeCount ?? 0
);

/**
 * Gets whether we've fetched fast match data
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} Whether we've already fetched fast match data
 */
export const getFastMatchesLoaded = createSelector(
  selectLikesYou,
  (likesYou) => likesYou.fastMatchesLoaded
);

/**
 * Gets whether we're currently fetching fast match data
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} Whether we're fetching fast match data
 */
export const getFastMatchesLoading = createSelector(
  selectLikesYou,
  (likesYou) => likesYou.fastMatchesLoading
);

/**
 * Gets the next page token for fast matches
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} Token for the next page of fast matches
 */
export const getFastMatchesNextPageToken = createSelector(
  selectLikesYou,
  (likesYou) => likesYou.nextPageToken
);

/**
 * Gets whether we're currently fetching fast match teasers data
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} Whether we're fetching fast match data
 */
export const getFastMatchTeasersLoading = createSelector(
  selectLikesYou,
  (likesYou) => likesYou.fastMatchTeasersLoading
);

/**
 * Gets whether we've fetched fast match teasers data
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} Whether we've already fetched fast match teasers data
 */
export const getFastMatchTeasersLoaded = createSelector(
  selectLikesYou,
  (likesYou) => likesYou.fastMatchTeasersLoaded
);

/**
 * Gets the list of fast match teasers IDs
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Array<string>} IDs of fast matches
 */
export const getFastMatchTeasersIds = createSelector(
  selectLikesYou,
  (likesYou) => likesYou.fastMatchTeasers
);

export const getFastMatchTeasers = createDenormalizedSelector(
  getFastMatchTeasersIds,
  selectEntitiesForProfiles,
  profileArray,
  CACHE_SIZE_ARRAY
);

/**
 * Gets gold message list teaser url
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} teaser url for gold message list item
 */
export const getRecentlyActiveGoldTeaserUrl = createSelector(
  selectLikesYou,
  (likesYou) => likesYou.recentlyActiveGoldTeaser?.url
);

/**
 * Gets gold message list teaser name
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} teaser name for gold message list item
 */
export const getRecentlyActiveGoldTeaserName = createSelector(
  selectLikesYou,
  (likesYou) => likesYou.recentlyActiveGoldTeaser?.name
);

export const getFastMatchesSwipedIds = createSelector(selectLikesYou, (likesYou) =>
  likesYou.fastMatchesSwiped?.map((item) => item.id)
);
